<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useFloatHubStore } from '/src/stores/floatHub'

const hubStore = useFloatHubStore()

const isDragging = ref(false)
const hasDragged = ref(false)
const dragStart = ref({ x: 0, y: 0 })
const startPos = ref({ x: 0, y: 0 })

const BALL_SIZE = 48
const PADDING = 24

const positionStyle = computed(() => ({
  left: `${hubStore.hubPosition.x}px`,
  top: `${hubStore.hubPosition.y}px`,
  width: `${BALL_SIZE}px`,
  height: `${BALL_SIZE}px`
}))

const handleMouseDown = (e) => {
  if (e.type === 'mousedown' && e.button !== 0) return
  isDragging.value = true
  hasDragged.value = false
  dragStart.value = { x: e.clientX || e.touches?.[0]?.clientX, y: e.clientY || e.touches?.[0]?.clientY }
  startPos.value = { ...hubStore.hubPosition }
  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
  document.addEventListener('touchmove', handleMouseMove, { passive: false })
  document.addEventListener('touchend', handleMouseUp)
  document.body.style.cursor = 'grabbing'
}

const handleMouseMove = (e) => {
  if (!isDragging.value) return
  e.preventDefault()
  const clientX = e.clientX || e.touches?.[0]?.clientX
  const clientY = e.clientY || e.touches?.[0]?.clientY
  if (clientX === undefined || clientY === undefined) return

  const deltaX = clientX - dragStart.value.x
  const deltaY = clientY - dragStart.value.y

  if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
    hasDragged.value = true
  }

  let newX = startPos.value.x + deltaX
  let newY = startPos.value.y + deltaY
  newX = Math.max(PADDING, Math.min(window.innerWidth - BALL_SIZE - PADDING, newX))
  newY = Math.max(PADDING, Math.min(window.innerHeight - BALL_SIZE - PADDING, newY))
  hubStore.setHubPosition({ x: newX, y: newY })
}

const handleMouseUp = () => {
  isDragging.value = false
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)
  document.removeEventListener('touchmove', handleMouseMove)
  document.removeEventListener('touchend', handleMouseUp)
  document.body.style.cursor = ''
}

const handleClick = () => {
  if (!hasDragged.value) {
    hubStore.toggleOpen()
  }
}

onMounted(() => {
  hubStore.loadHubPosition()
})

onUnmounted(() => {
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)
  document.removeEventListener('touchmove', handleMouseMove)
  document.removeEventListener('touchend', handleMouseUp)
})
</script>

<template>
  <div
    class="hub-ball"
    :class="{ 'is-dragging': isDragging, 'is-open': hubStore.isOpen }"
    :style="positionStyle"
    @mousedown="handleMouseDown"
    @touchstart.passive="handleMouseDown"
    @click="handleClick"
  >
    <span class="hub-icon" :class="{ rotated: hubStore.isOpen }">
      ✦
    </span>
  </div>
</template>

<style scoped>
.hub-ball {
  position: fixed;
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
  cursor: grab;
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
              box-shadow 0.25s ease,
              background 0.25s ease;
  user-select: none;
}

.hub-ball:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(14, 165, 233, 0.5);
}

.hub-ball.is-dragging {
  cursor: grabbing;
  transform: scale(1.1);
  box-shadow: 0 8px 24px rgba(14, 165, 233, 0.6);
}

.hub-ball.is-open {
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  box-shadow: 0 4px 16px rgba(14, 165, 233, 0.5);
}

.hub-ball.is-open:hover {
  box-shadow: 0 6px 24px rgba(14, 165, 233, 0.6);
}

.hub-ball:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.3), 0 4px 12px rgba(14, 165, 233, 0.4);
}

.hub-icon {
  font-size: 22px;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  line-height: 1;
}

.hub-icon.rotated {
  transform: rotate(45deg);
}
</style>
